<template>
  <section
    class="RunCarouselView"
    data-testid="runCarousel"
    data-theme="light"
    :data-direction="direction"
    :class="{
      'RunCarouselView--isPast': isSelectedRunPast,
    }"
  >
    <header
      class="RunCarouselView__header"
      data-testid="runCarouselHeader"
    >
      <h2
        class="RunCarouselView__title"
      >
        <Transition>
          <span
            v-if="isSelectedRunPast"
          >Past runs</span>
          <span
            v-else-if="isSelectedRunToday"
          >Today’s runs</span>
          <span
            v-else
          >Upcoming runs</span>
        </Transition>
      </h2>
    </header>
    <div class="RunCarouselView__trackContainer">
      <div
        ref="trackRef"
        class="RunCarouselView__track"
        data-testid="runCarouselTrack"
      >
        <PastRunTimelineView
          ref="pastRunTimelineRef"
          class="RunCarouselView__item"
          data-item-type="past"
          :aria-current="!selectedRunId && selectedItemType === 'past'"
        />
        <RunCardView
          v-for="run in currentRuns"
          :key="run.id"
          ref="itemRefs"
          v-bind="{ run }"
          class="RunCarouselView__item"
          :aria-current="run.id === selectedRunId"
        />
        <FutureRunTimelineView
          ref="futureRunTimelineRef"
          class="RunCarouselView__item"
          data-item-type="future"
          :aria-current="!selectedRunId && selectedItemType === 'future'"
        />
      </div>
    </div>
    <footer
      class="RunCarouselView__footer"
    >
      <ol
        class="RunCarouselView__paginationList"
        data-testid="runCarouselPagination"
      >
        <li class="RunCarouselView__paginationItem">
          <button
            class="RunCarouselView__paginationButton--timeline RunCarouselView__paginationButton"
            type="button"
            data-testid="runCarouselPaginationDot--past"
            aria-label="Past runs"
            :aria-current="!selectedRunId && selectedItemType === 'past'"
            @click="scrollToItem(pastRunTimelineRef?.$el)"
          />
        </li>
        <li
          v-for="run in currentRuns"
          :key="run.id"
          class="RunCarouselView__paginationItem"
        >
          <button
            class="RunCarouselView__paginationButton"
            type="button"
            :data-testid="`runCarouselPaginationDot--${run.id}`"
            :aria-label="getRunLabel(run)"
            :aria-current="run.id === selectedRunId"
            @click="scrollToRun(run.id)"
          />
        </li>
        <li class="RunCarouselView__paginationItem">
          <button
            class="RunCarouselView__paginationButton--timeline RunCarouselView__paginationButton"
            type="button"
            data-testid="runCarouselPaginationDot--future"
            aria-label="Upcoming runs"
            :aria-current="!selectedRunId && selectedItemType === 'future'"
            @click="scrollToItem(futureRunTimelineRef?.$el)"
          />
        </li>
      </ol>
    </footer>
  </section>
</template>

<script lang="ts" setup>
import useNow from "@/composables/useNow";
import useRealtimeChannel from "@/composables/useRealtimeChannel";
import { supabaseClient } from "@/lib/database/supabase";
import useRunCarouselStore, { RunForCarousel } from "@/stores/useRunCarouselStore";
import { TZDate } from "@date-fns/tz";
import { Status, StatusSummaryWithPlayer } from "@shared/lib/database/query.types";
import { RealtimePostgresDeletePayload, RealtimePostgresInsertPayload, RealtimePostgresUpdatePayload } from "@supabase/supabase-js";
import { format, isAfter, isBefore, isSameDay, parseISO, startOfDay } from "date-fns";
import { storeToRefs } from "pinia";
import { ComponentPublicInstance, computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";
import FutureRunTimelineView from "./FutureRunTimelineView.vue";
import PastRunTimelineView from "./PastRunTimelineView.vue";
import RunCardView from "./RunCardView.vue";

const {
  currentRuns,
} = storeToRefs(useRunCarouselStore());

const {
  now,
} = useNow();

useRealtimeChannel("statuses", {
  handleUpsert: handleStatusUpsert,
  handleDelete: handleStatusDelete,
});

const direction = ref<"left" | "right">("right");
const selectedRunId = ref<string | null>(null);
const itemRefs = useTemplateRef<Array<ComponentPublicInstance>>("itemRefs");
const pastRunTimelineRef = useTemplateRef<InstanceType<typeof PastRunTimelineView>>("pastRunTimelineRef");
const futureRunTimelineRef = useTemplateRef<InstanceType<typeof FutureRunTimelineView>>("futureRunTimelineRef");
const trackRef = useTemplateRef<HTMLElement>("trackRef");

const intersectionObserver = new IntersectionObserver(handleIntersection, {
  threshold: 0.5,
});
const resizeObserver = new ResizeObserver(handleResize);
const itemTypes = [
  "past",
  "current",
  "future",
] as const;
const selectedItemType = ref<(typeof itemTypes)[number]>("current");

onMounted(() => {
  if (trackRef.value) resizeObserver.observe(trackRef.value);
});

onBeforeUnmount(() => {
  intersectionObserver.disconnect();
  resizeObserver.disconnect();
});

const statuses = computed(() => {
  return currentRuns.value.flatMap((run) => run.statuses) || [];
});

const selectedItemRef = computed(() => {
  return itemRefs.value?.find((itemRef) => itemRef.$el.dataset.runId === selectedRunId.value);
});

const selectedRun = computed(() => {
  return currentRuns.value.find((run) => run.id === selectedRunId.value);
});

const isSelectedRunPast = computed(() => {
  if (!selectedRun.value) return selectedItemType.value === "past";

  const startDateInTimezone = new TZDate(parseISO(selectedRun.value.start_at), selectedRun.value.court?.timezone || "America/New_York");

  return isBefore(startOfDay(startDateInTimezone), startOfDay(now.value));
});

const isSelectedRunToday = computed(() => {
  if (!selectedRun.value) return false;

  const startDateInTimezone = new TZDate(parseISO(selectedRun.value.start_at), selectedRun.value.court?.timezone || "America/New_York");

  return isSameDay(startDateInTimezone, now.value);
});

watch([selectedRunId, selectedItemType], ([newId, newItemType], [oldId, oldItemType]) => {
  const newIndex = currentRuns.value.findIndex((run) => run.id === newId);
  const oldIndex = currentRuns.value.findIndex((run) => run.id === oldId);
  const newItemTypeIndex = itemTypes.indexOf(newItemType);
  const oldItemTypeIndex = itemTypes.indexOf(oldItemType);

  direction.value = newItemTypeIndex < oldItemTypeIndex || newIndex < oldIndex
    ? "left"
    : "right";
});

watch(currentRuns, () => {
  nextTick().then(() => updateIntersectionObservers());
  updateSelectedRun();
}, {
  immediate: true,
});

function updateIntersectionObservers () {
  intersectionObserver.disconnect();

  if (itemRefs.value) {
    itemRefs.value.forEach((itemRef) => {
      intersectionObserver.observe(itemRef.$el);
    });
  }

  if (pastRunTimelineRef.value) {
    intersectionObserver.observe(pastRunTimelineRef.value.$el);
  }

  if (futureRunTimelineRef.value) {
    intersectionObserver.observe(futureRunTimelineRef.value.$el);
  }
}

function updateSelectedRun () {
  if (!selectedRunId.value && selectedItemType.value === "current") {
    const closestRun = currentRuns.value.find((run) => {
      const startDateInTimezone = new TZDate(parseISO(run.start_at), run.court?.timezone || "America/New_York");

      return isSameDay(startDateInTimezone, now.value) || isAfter(startDateInTimezone, now.value);
    });
    const run = closestRun || currentRuns.value.at(-1);

    setSelectedRunId(run?.id || null);
    scrollToSelectedRun();
  }
}

function setSelectedRunId (runId: string | null) {
  selectedRunId.value = runId;
}

function scrollToItem (el?: HTMLElement, {
  behavior = "smooth",
}: {
  behavior?: ScrollBehavior,
} = {}) {
  if (!trackRef.value || !el) return;

  trackRef.value.scrollTo({
    left: el.offsetLeft,
    behavior,
  });
}

function scrollToRun (runId: string) {
  scrollToItem(itemRefs.value?.find((itemRef) => itemRef.$el.dataset.runId === runId)?.$el);
}

function scrollToSelectedRun () {
  scrollToItem(selectedItemRef.value?.$el, { behavior: "instant" });
}

function getRunLabel (run: RunForCarousel) {
  const startDate = new TZDate(parseISO(run.start_at), run.court?.timezone || "America/New_York");

  return `Go to run on ${format(startDate, "MMMM d")}`;
}

function addOrReplaceStatus (status: StatusSummaryWithPlayer) {
  const existingStatus = statuses.value.find((_status) => _status.run_id === status.run_id && _status.player_id == status.player_id);

  if (existingStatus) {
    Object.assign(existingStatus, status);
  } else {
    const run = currentRuns.value.find((run) => run.id === status.run_id);

    if (run) run.statuses.push(status);
  }
}

function removeStatus (status: Partial<Status>) {
  const run = currentRuns.value.find((run) => run.id === status.run_id);
  if (!run) return;

  const index = run.statuses.findIndex((_status) => _status.player_id === status.player_id);
  if (index !== -1) run.statuses.splice(index, 1);
}

async function handleStatusUpsert (payload: RealtimePostgresInsertPayload<Status> | RealtimePostgresUpdatePayload<Status>) {
  const {
    data: status,
  } = await supabaseClient
    .from("statuses")
    .select("run_id, player_id, probability, note, created_at, updated_at, player:players(id, name, theme, avatar_storage_path)")
    .match({
      run_id: payload.new.run_id, // TODO: add primary key to statuses
      player_id: payload.new.player_id,
    })
    .maybeSingle();

  if (status) addOrReplaceStatus(status);
}

function handleStatusDelete (payload: RealtimePostgresDeletePayload<Status>) {
  removeStatus(payload.old);
}

function handleIntersection (entries: Array<IntersectionObserverEntry>) {
  const entry = entries.find((entry) => entry.isIntersecting);
  if (!entry) return;

  const el = entry.target as HTMLElement;

  if (el.dataset.runId) {
    selectedItemType.value = "current";
    setSelectedRunId(el.dataset.runId);
  } else {
    selectedItemType.value = el.dataset.itemType as "past" | "future";
    setSelectedRunId(null);
  }
}

function handleResize () {
  scrollToSelectedRun();
}
</script>

<style lang="scss" scoped>
.RunCarouselView {
  position: relative;

  display: grid;
  grid-template-rows: min-content auto;
  gap: 16px;
  padding: 24px 0;

  background-color: var(--themeColorNeutral0);
}

.RunCarouselView__header {
  padding: var(--fontSafariOffset) 16px 0;
  overflow: hidden;
}

.RunCarouselView__title {
  position: relative;

  display: flex;

  color: var(--themeColorNeutral600);

  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  text-transform: uppercase;
}

.RunCarouselView__title span {
  display: flex;
  align-items: center;
  height: 100%;

  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  text-transform: uppercase;
  white-space: nowrap;

  transition: 275ms;
  transition-property: transform, opacity;

  &.v-leave-active {
    position: absolute;
    top: 0;
    left: 0;
  }

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }

  [data-direction="left"] &.v-enter-from,
  [data-direction="right"] &.v-leave-to {
    transform: translateX(-40px);
  }

  [data-direction="right"] &.v-enter-from,
  [data-direction="left"] &.v-leave-to {
    transform: translateX(40px);
  }
}

.RunCarouselView__trackContainer {
  position: relative;
  overflow: hidden;
}

.RunCarouselView__track {
  display: grid;
  grid-auto-columns: calc(100% - 24px);
  grid-auto-flow: column;
  gap: 16px;
  padding: 0 16px;
  overflow-x: auto;

  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &:has(> :only-child) {
    grid-auto-columns: 100%;
  }
}

.RunCarouselView__item {
  scroll-snap-align: center;
  scroll-snap-stop: always;
  transition: opacity 350ms;

  &[aria-current="false"] {
    opacity: 0.5;
  }
}

.RunCarouselView__footer {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.RunCarouselView__paginationList {
  display: flex;
}

.RunCarouselView__paginationItem {
  display: flex;
}

.RunCarouselView__paginationButton {
  position: relative;

  display: grid;
  place-items: center;
  width: 24px;
  aspect-ratio: 1;

  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    width: 10px;
    aspect-ratio: 1;

    border-radius: 50%;
    background-color: var(--themeColorNeutral900);

    transition: 150ms opacity;

    content: "";
  }

  &[aria-current="false"]::after {
    opacity: 0.2;
  }
}

.RunCarouselView__paginationButton--timeline {
  &::after {
    border-radius: 2px;
  }
}
</style>
